import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Pagination,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import venueActions from "../../redux/venue/actions";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";

/**
 * Duplicate candidates (same name as per HSC, father, mother, DOB and spouse) are
 * seated in the same venue and batch by seat allocation. Allocation refuses to run
 * until every course in the run has been refreshed after its last payment, so this
 * panel shows each course's refresh state with a Refresh button, plus a review list
 * and a post-allocation report.
 */

// Courses whose candidates a run includes. Mirrors getRunCourseIds in the backend
// (services/backOffice/duplicateCandidates.js).
const getRunCourseIds = (courseid, ispcm, ispcb, attempt) => {
  const c = parseInt(courseid);
  if (c === 124 && (ispcm == 1 || ispcb == 1) && attempt == 2) return [124, 153];
  if (c === 125 && attempt == 1) return [125, 155];
  return [c];
};

const PAGE_SIZE = 20;
const fmtDateTime = (v) => (v ? dayjs(v).format("DD-MM-YYYY hh:mm A") : "-");
const fmtDate = (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "-");

const STATUS_COLOR = {
  TOGETHER: "success",
  SPLIT: "error",
  PARTIAL: "warning",
  UNALLOCATED: "error",
};

export default function DuplicateCandidates({ courseid, ispcm, ispcb, noofattempt }) {
  const dispatch = useDispatch();
  const { duplicateSummary, duplicateGroups, duplicateReport } = useSelector((state) => state.venueReducer);
  const { courseList } = useSelector((state) => state.masterReducer);
  const { backofficeTokenDetails } = useSelector((state) => state.authReducer);

  const [refreshing, setRefreshing] = useState({});
  const [reviewCourse, setReviewCourse] = useState(null);
  const [page, setPage] = useState(1);
  const [notDuplicate, setNotDuplicate] = useState(null); // { groupno, groupkey }
  const [remarks, setRemarks] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [problemsOnly, setProblemsOnly] = useState(true);

  const runCourseIds = courseid ? getRunCourseIds(courseid, ispcm, ispcb, noofattempt) : [];
  const courseName = (id) => courseList?.all?.find((c) => c.courseid == id)?.coursename || `Course ${id}`;

  useEffect(() => {
    runCourseIds.forEach((id) =>
      dispatch({ type: venueActions.GET_DUPLICATE_SUMMARY, payload: { courseid: id } })
    );
    // eslint-disable-next-line
  }, [courseid, ispcm, ispcb, noofattempt, dispatch]);

  if (!courseid) return null;

  const handleRefresh = (id) => {
    dispatch({
      type: venueActions.REFRESH_DUPLICATES,
      payload: {
        courseid: id,
        userid: backofficeTokenDetails?.id,
        setLoading: (v) => setRefreshing((s) => ({ ...s, [id]: v })),
      },
    });
  };

  const openReview = (id) => {
    setReviewCourse(id);
    setPage(1);
    dispatch({ type: venueActions.GET_DUPLICATE_GROUPS, payload: { courseid: id, page: 1, size: PAGE_SIZE } });
  };

  const changePage = (_, value) => {
    setPage(value);
    dispatch({ type: venueActions.GET_DUPLICATE_GROUPS, payload: { courseid: reviewCourse, page: value, size: PAGE_SIZE } });
  };

  const confirmNotDuplicate = () => {
    dispatch({
      type: venueActions.REVIEW_DUPLICATE_GROUP,
      payload: {
        courseid: reviewCourse,
        groupkey: notDuplicate.groupkey,
        decision: "NOT_DUPLICATE",
        remarks,
        userid: backofficeTokenDetails?.id,
        page,
        size: PAGE_SIZE,
      },
    });
    setNotDuplicate(null);
    setRemarks("");
  };

  const openReport = () => {
    setReportOpen(true);
    dispatch({
      type: venueActions.GET_DUPLICATE_REPORT,
      payload: { courseid, ispcm, ispcb, attempt: noofattempt },
    });
  };

  const reportGroups = (duplicateReport?.groups || []).filter(
    (g) => !problemsOnly || g.status !== "TOGETHER"
  );

  return (
    <Box sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", p: 2, mb: 1 }}>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>Duplicate candidates</Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
        Candidates with the same name, father, mother, date of birth and spouse are seated in
        the same venue and batch. Refresh after the last payment, before running allocation.
      </Typography>

      {runCourseIds.map((id) => {
        const s = duplicateSummary?.[id];
        return (
          <Box
            key={id}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1 }}
          >
            <Typography sx={{ minWidth: 180, fontWeight: 500 }}>{courseName(id)}</Typography>
            {!s?.refreshed_at ? (
              <Chip size="small" color="error" label="Never refreshed" />
            ) : s.isstale ? (
              <Chip size="small" color="warning" label="Out of date: new payments since refresh" />
            ) : (
              <Chip size="small" color="success" label="Up to date" />
            )}
            <Typography variant="body2">
              Last refreshed: {fmtDateTime(s?.refreshed_at)} · Groups: {s?.groupcount ?? 0} ·
              Candidates: {s?.candidatecount ?? 0} · Paid: {s?.paidcount ?? "-"}
            </Typography>
            <BluePrimaryButton
              title={refreshing[id] ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Refresh Duplicates"}
              disabled={!!refreshing[id]}
              handleSubmit={() => handleRefresh(id)}
            />
            <Button
              variant="outlined"
              sx={{ borderRadius: "40px", height: 40 }}
              disabled={!s?.groupcount}
              onClick={() => openReview(id)}
            >
              View Duplicates
            </Button>
          </Box>
        );
      })}

      <Button variant="outlined" sx={{ borderRadius: "40px", height: 40, mt: 0.5 }} onClick={openReport}>
        Duplicate Allocation Report
      </Button>

      {/* Review dialog */}
      <Dialog open={!!reviewCourse} onClose={() => setReviewCourse(null)} maxWidth="xl" fullWidth>
        <DialogTitle>
          Duplicate candidates: {reviewCourse && courseName(reviewCourse)} ({duplicateGroups?.total || 0} groups)
        </DialogTitle>
        <DialogContent dividers>
          {(duplicateGroups?.groups || []).map((g) => (
            <Box key={g.groupno} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "#F5F5F5" }}>
                <Typography sx={{ fontWeight: 600 }}>Group {g.groupno}</Typography>
                {g.ispwdgroup && <Chip size="small" color="info" label="PWD: placed at a PWD-suitable venue" />}
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => setNotDuplicate({ groupno: g.groupno, groupkey: g.groupkey })}
                >
                  Not a duplicate
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Application No</TableCell>
                    <TableCell>Name as per HSC</TableCell>
                    <TableCell>Father</TableCell>
                    <TableCell>Mother</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Spouse</TableCell>
                    <TableCell>PWD</TableCell>
                    <TableCell>Registered</TableCell>
                    <TableCell>Preference districts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.members.map((m) => (
                    <TableRow key={m.candidateid}>
                      <TableCell>
                        {m.isleader && <Chip size="small" color="primary" label="Leader" />}
                      </TableCell>
                      <TableCell>{m.applicantid || m.candidateid}</TableCell>
                      <TableCell>{m.name_as_per_hsc || m.candidatename}</TableCell>
                      <TableCell>{m.fathersname}</TableCell>
                      <TableCell>{m.mothername}</TableCell>
                      <TableCell>{fmtDate(m.dob)}</TableCell>
                      <TableCell>{m.spousename || "-"}</TableCell>
                      <TableCell>{m.ispwd ? "Yes" : "No"}</TableCell>
                      <TableCell>{fmtDateTime(m.createddate)}</TableCell>
                      <TableCell>
                        {[m.preference1districtid, m.preference2districtid, m.preference3districtid, m.preference4districtid]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
          {duplicateGroups?.total > PAGE_SIZE && (
            <Pagination
              count={Math.ceil(duplicateGroups.total / PAGE_SIZE)}
              page={page}
              onChange={changePage}
              sx={{ display: "flex", justifyContent: "center" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewCourse(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Not-a-duplicate confirmation */}
      <Dialog open={!!notDuplicate} onClose={() => setNotDuplicate(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark group {notDuplicate?.groupno} as not a duplicate?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            These candidates will be allocated independently, and the group will not be flagged
            again on later refreshes.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Remarks (optional)"
            value={remarks}
            inputProps={{ maxLength: 500 }}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotDuplicate(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmNotDuplicate}>
            Mark not a duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-allocation report */}
      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Duplicate allocation report: {courseName(courseid)}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
            {Object.entries(duplicateReport?.counts || {}).map(([status, count]) => (
              <Chip key={status} color={STATUS_COLOR[status]} label={`${status}: ${count}`} />
            ))}
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              control={<Switch checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />}
              label="Only groups needing attention"
            />
          </Box>
          {reportGroups.length === 0 ? (
            <Typography>No groups to show.</Typography>
          ) : (
            reportGroups.map((g) => (
              <Box key={g.groupno} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "#F5F5F5" }}>
                  <Typography sx={{ fontWeight: 600 }}>Group {g.groupno}</Typography>
                  <Chip size="small" color={STATUS_COLOR[g.status]} label={g.status} />
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Application No</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Venue</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell>Exam date</TableCell>
                      <TableCell>Roll No</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.members.map((m) => (
                      <TableRow key={m.candidateid}>
                        <TableCell>{m.isleader && <Chip size="small" color="primary" label="Leader" />}</TableCell>
                        <TableCell>{m.applicantid || m.candidateid}</TableCell>
                        <TableCell>{m.name}</TableCell>
                        <TableCell>{m.venuename || "Not allocated"}</TableCell>
                        <TableCell>{m.batchname || "-"}</TableCell>
                        <TableCell>{fmtDate(m.examdate)}</TableCell>
                        <TableCell>{m.rollno || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
